import { Popover, Transition } from '@headlessui/react';
import { ButtonCTASmall, LoadingSpinner } from 'components/ui';
import querystring from 'querystring';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import { useAppSelector } from 'store';
import { composableFiltersState } from 'store/composableFiltersSlice';
import { useSaveSegmentMutation } from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';
import { FiltersDto } from './FiltersDto';

export const SaveSegment = ({ children }: { children: ReactNode }) => {
  const [segmentName, setSegmentName] = useState('');
  const [description, setDescription] = useState('');

  const { query } = useParseURLQueryParams();

  const [saveSegment, { isLoading }] = useSaveSegmentMutation();

  const { selected } = useAppSelector(composableFiltersState);

  const handleSaveSegment = async (close: Function) => {
    if (!!!segmentName) return;

    const params = querystring.stringify({
      store_name: query.store_name,
      description: description,
      segment_name: segmentName,
    });

    const body = new FiltersDto(selected.filters).generateBodyPayload();

    saveSegment({ serializedParams: params, body })
      .unwrap()
      .then(() => {
        close();
      });
  };

  useEffect(() => {
    return () => {
      setSegmentName('');
      setDescription('');
    };
  }, []);

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            className={`
                ${open ? '' : 'text-opacity-90'}
                text-segments-link group px-3 py-2 text-base font-medium hover:text-opacity-100 `}
          >
            {children}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-10 w-screen max-w-sm mt-2 transform -translate-x-1/2 left-1/2 lg:max-w-md ">
              <div className="bg-white shadow-2xl rounded-3xl ring-1 ring-black ring-opacity-5">
                <div className="pt-6 px-7">
                  <p className="font-bold">Save as a new segment</p>
                </div>
                <form
                  className="px-6 py-2 text-gray-700"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSaveSegment(close);
                  }}
                >
                  <div className="mt-2 space-y-4">
                    <input
                      tabIndex={0}
                      type="text"
                      id="segment-name"
                      className="flex-1 w-full h-12 px-5 placeholder-black border border-black border-solid border-opacity-20 rounded-segments-10px placeholder-opacity-30 focus:border-segments-accent-color-1 focus:ring-segments-accent-color-1 disabled:bg-segments-gray"
                      value={segmentName}
                      onChange={(e) => setSegmentName(e.target.value)}
                      spellCheck="false"
                      required
                      placeholder="Name"
                    />
                    <div tabIndex={1}>
                      <input
                        type="text"
                        id="segment-description"
                        className="flex-1 w-full h-12 px-5 placeholder-black border border-black border-solid border-opacity-20 rounded-segments-10px placeholder-opacity-30 focus:border-segments-accent-color-1 focus:ring-segments-accent-color-1 disabled:bg-segments-gray"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        spellCheck="false"
                        placeholder="Description"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end mt-4 mb-4 space-x-4">
                    <div>
                      <ButtonCTASmall
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          close();
                        }}
                      >
                        <p className="px-4 font-normal text-segments-13px">
                          Cancel
                        </p>
                      </ButtonCTASmall>
                    </div>
                    <div>
                      <ButtonCTASmall
                        variant="main"
                        type="submit"
                        disabled={!!!segmentName}
                      >
                        <div className="w-20 font-bold text-segments-13px">
                          {isLoading ? (
                            <LoadingSpinner className="w-5 h-5 mx-auto text-white" />
                          ) : (
                            'Save'
                          )}
                        </div>
                      </ButtonCTASmall>
                    </div>
                  </div>
                </form>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};
